import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, PenTool, ExternalLink } from 'lucide-react';
import { RutField, fillEmpty, splitName } from '../RutField';

const input = 'w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500';
const STATUS = { pendiente: 'text-amber-300', firmado: 'text-emerald-300', rechazado: 'text-red-300' };

export const FirmaWebSign = ({ body, defaultSigner }) => {
  const { api } = useAuth();
  const [configured, setConfigured] = useState(null);
  const [signer, setSigner] = useState({});
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const touched = useRef(new Set());
  useEffect(() => {
    if (!defaultSigner) return;
    setSigner((s) => {
      const next = { ...s };
      Object.entries(defaultSigner).forEach(([k, v]) => { if (!touched.current.has(k)) next[k] = v || ''; });
      return next;
    });
  }, [defaultSigner]);

  const load = () => api.get('/labor/finiquito/signatures').then((r) => setItems(r.data.signatures)).catch(() => {});
  useEffect(() => {
    api.get('/labor/finiquito/firmaweb-status').then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false));
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post('/labor/finiquito/sign', { ...body, signer: { ...signer, rut: (signer.rut || '').replace(/\./g, '') } });
      toast.success('Enviado a FirmaWeb para firma electrónica avanzada');
      if (r.data.signing_url) window.open(r.data.signing_url, '_blank', 'noopener');
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? 'Revisa los datos del notario (RUT sin puntos, ej. 12345678-9)' : d || 'No se pudo enviar a FirmaWeb');
    } finally { setBusy(false); }
  };
  const f = (k, ph) => <input data-testid={`firmaweb-signer-${k}`} value={signer[k] || ''} onChange={(e) => { touched.current.add(k); setSigner({ ...signer, [k]: e.target.value }); }} placeholder={ph} className={input} />;

  return (
    <div data-testid="firmaweb-sign" className="border border-blue-500/30 rounded-xl p-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-white flex items-center gap-2"><PenTool className="w-4 h-4 text-blue-400" />Enviar a firmar con FirmaWeb</p>
        <p className="text-xs text-slate-400">El notario recibe el PDF (finiquito + ratificación) y lo firma en línea con firma electrónica avanzada.</p>
      </div>
      {configured === false && (
        <p data-testid="firmaweb-not-configured" className="text-xs bg-slate-800 border border-slate-600 text-slate-300 rounded-lg px-3 py-2">
          Falta conectar tu cuenta FirmaWeb (usuario y clave de la API). Mientras tanto, descarga el Word o PDF con la leyenda de firma electrónica avanzada.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        {f('nombre', 'Nombre del notario')}
        {f('apellidoPaterno', 'Apellido paterno')}
        {f('apellidoMaterno', 'Apellido materno')}
        <RutField testid="firmaweb-signer-rut" value={signer.rut} onChange={(v) => { touched.current.add('rut'); setSigner((s) => ({ ...s, rut: v })); }} placeholder="RUT (12.345.678-9)" className={input}
          onLookup={(d) => { const n = splitName(d.name); setSigner((s) => fillEmpty(s, { nombre: n.nombre, apellidoPaterno: n.paterno, apellidoMaterno: n.materno })); }} />
        <div className="sm:col-span-2">{f('email', 'Email del notario')}</div>
      </div>
      <button data-testid="firmaweb-send-btn" onClick={send} disabled={busy || !configured} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenTool className="w-4 h-4" />}Enviar a firma electrónica avanzada
      </button>
      {items.length > 0 && (
        <ul data-testid="firmaweb-signatures" className="text-xs space-y-1">
          {items.map((s) => (
            <li key={s.id} className="flex justify-between gap-2 text-slate-400">
              <span>{s.worker || 'Finiquito'}{s.repertorio ? ` · Rep. ${s.repertorio}` : ''}</span>
              <span className={`flex items-center gap-1 ${STATUS[s.status]}`}>{s.status}{s.signing_url && s.status === 'pendiente' && <a href={s.signing_url} target="_blank" rel="noopener noreferrer" aria-label="Abrir firma"><ExternalLink className="w-3 h-3" /></a>}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
